/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import CustomTabPanel from "components/CustomTabPanel";
import data from "layouts/dashboard/components/Projects/data";
import useApi from "components/Utils/api";
import toast from "utils/toast";

function a11yProps(index) {
  return { id: `merchant-tab-${index}`, "aria-controls": `merchant-tabpanel-${index}` };
}

const tabSx = ({ palette }) => ({
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  minHeight: "unset",
  py: 2,
  px: 3,
  color: `${palette.grey[700]} !important`,
  "&.Mui-selected": {
    color: `${palette.info.main} !important`,
  },
});

function DetailField({ label, value, required }) {
  return (
    <TextField
      label={required ? `${label} *` : label}
      value={value ?? ""}
      size="small"
      fullWidth
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          backgroundColor: "transparent",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "grey.300",
        },
        "& .MuiInputBase-input": {
          fontWeight: 400,
          fontSize: "0.95rem",
          lineHeight: "20px",
          color: "dark.main",
          paddingTop: "10px",
          paddingBottom: "10px",
        },
        "& .MuiInputLabel-root": {
          fontSize: "0.8rem",
          color: "text.secondary",
        },
      }}
    />
  );
}

function MyMerchant() {
  const { merchantAllUsersTablecolumns } = data();
  const [merchant, setMerchant] = useState(null);
  const [users, setUsers] = useState([]);
  const [merchantFiles, setMerchantFiles] = useState([]);
  const [referenceCompanyList, setReferenceCompanyList] = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [logoUrl, setLogoUrl] = useState(null);
  const midRef = useRef(null);
  const logoUrlRef = useRef(null);
  const api = useApi();

  useEffect(() => {
    // Step 1: GET /merchant → get companyId
    api.get("merchant")
      .then((res) => {
        const body = res.data?.body;
        if (!Array.isArray(body) || body.length === 0) {
          toast.error("No merchant data found");
          return;
        }
        const { companyId } = body[0];

        // Step 2: POST /merchant/single → get full details
        api.post("merchant/single", { companyId: String(companyId) })
          .then((res2) => {
            if (res2.data?.statusCodeValue !== 200) {
              toast.error("Failed to load merchant details");
              return;
            }
            const responseBody = res2.data.body;
            const merchantData = responseBody?.merchant;
            midRef.current = merchantData?.mid ?? null;
            setMerchant(merchantData);
            setUsers(responseBody?.users ?? []);
            setMerchantFiles(responseBody?.merchantFiles ?? []);
            setReferenceCompanyList(responseBody?.referenceCompany ?? []);
          })
          .catch(() => toast.error("Failed to load merchant details"));
      })
      .catch(() => toast.error("Failed to load merchant"));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (tabValue === 4 && midRef.current && !accountInfo) {
      api.get(`merchant/${midRef.current}/account-info`)
        .then((res) => setAccountInfo(res.data?.body ?? null))
        .catch(() => toast.error("Failed to load QR info"));
    }
  }, [tabValue]); // eslint-disable-line

  // Fetch logo with authorization
  useEffect(() => {
    const logoFile = merchantFiles.find(f => f.category === "LOGO");

    if (logoFile) {
      api.get(`merchant/file/${logoFile.id}`, { responseType: "blob" })
        .then((res) => {
          // Revoke old URL if it exists
          if (logoUrlRef.current) {
            window.URL.revokeObjectURL(logoUrlRef.current);
          }
          const url = window.URL.createObjectURL(new Blob([res.data]));
          logoUrlRef.current = url;
          setLogoUrl(url);
        })
        .catch(() => {
          setLogoUrl(null);
          toast.error("Failed to load merchant logo");
        });
    } else {
      setLogoUrl(null);
    }

    // Cleanup on unmount
    return () => {
      if (logoUrlRef.current) {
        window.URL.revokeObjectURL(logoUrlRef.current);
      }
    };
  }, [merchantFiles]); // eslint-disable-line

  const bank = merchant?.merchantBankDetails?.[0];

  const referenceCompanyName = referenceCompanyList.find(
    (c) => c.referenceId === merchant?.referenceId
  )?.referenceCompanyName ?? merchant?.referenceCompanyName;

  const masterTypeLabel = merchant?.microAtmStatus === "M"
    ? "Merchant"
    : "Merchant and Business Correspondence";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        {/* Header */}
        <MDBox mb={2}>
          <MDTypography variant="h5" fontWeight="bold">
            {merchant?.companyName ?? "My Merchant"}
          </MDTypography>
          {merchant?.mid && (
            <MDTypography variant="button" color="text" fontWeight="regular">
              MID: {merchant.mid}
            </MDTypography>
          )}
        </MDBox>

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{
            minHeight: "unset",
            width: "fit-content",
            backgroundColor: "transparent !important",
            borderRadius: 0,
            p: 0,
            borderBottom: "1px solid",
            borderColor: "grey.300",
            "& .MuiTabs-indicator": {
              height: "2px",
              borderRadius: 0,
              boxShadow: "none",
              backgroundColor: "info.main",
            },
          }}
        >
          <Tab label="Office Details" sx={tabSx} {...a11yProps(0)} />
          <Tab label="Bank Details" sx={tabSx} {...a11yProps(1)} />
          <Tab label="Other Details" sx={tabSx} {...a11yProps(2)} />
          <Tab label="Users" sx={tabSx} {...a11yProps(3)} />
          <Tab label="QR" sx={tabSx} {...a11yProps(4)} />
        </Tabs>

        {merchant ? (
          <>
            {/* ── Tab 0: Office Details ── */}
            <CustomTabPanel value={tabValue} index={0} contentSx={{ px: 0, pt: 5 }}>
              <Grid container columnSpacing={4} rowSpacing={2} sx={{ maxWidth: "760px" }}>
                <Grid item xs={12} md={6}><DetailField label="Registered Business Name" value={merchant.companyName} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Type of Entity" value={merchant.typeEstablishment} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Registered Business Address" value={merchant.address} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Actual Business Address" value={merchant.addressRes} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Years in Business" value={merchant.establishmentYrs} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Tax Registration No" value={merchant.saleTaxNo} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Corporate Tax No" value={merchant.tinNo} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Name of the Owner" value={merchant.ownerName} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Authorized Person of Company" value={merchant.authorizedPerson} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Mobile Number" value={merchant.mobileNo} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Email Id" value={merchant.emailId} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Office Phone" value={merchant.officeNo} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Organization Name" value={referenceCompanyName} required /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Master Type" value={masterTypeLabel} required /></Grid>
              </Grid>
            </CustomTabPanel>

            {/* ── Tab 1: Bank Details ── */}
            <CustomTabPanel value={tabValue} index={1} contentSx={{ px: 0, pt: 5 }}>
              <Grid container columnSpacing={4} rowSpacing={2} sx={{ maxWidth: "760px" }}>
                <Grid item xs={12} md={6}><DetailField label="Currency" value={bank?.currency} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Bank Name" value={bank?.bank?.bankName ?? bank?.bankName} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Account Name" value={bank?.branch} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Account Number" value={bank?.accountNumber} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Branch / Branch Code" value={bank?.bankCode} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Expected Card Business / Month" value={merchant.expectedCardBusinessPerMonth} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Average Bill Amount" value={merchant.averageBillAmount} /></Grid>
                <Grid item xs={12} md={6}><DetailField label="Turnover Year 1" value={merchant.turnover1 ?? merchant.turnoverYear1} /></Grid>
              </Grid>
            </CustomTabPanel>

            {/* ── Tab 2: Other Details ── */}
            <CustomTabPanel value={tabValue} index={2} contentSx={{ px: 0, pt: 5 }}>
              {(() => {
                const logoFile = merchantFiles.find(f => f.category === "LOGO");
                return logoFile ? (
                  <>
                    <MDTypography variant="caption" fontWeight="medium" color="text" mb={0.5} display="block">
                      Merchant Logo
                    </MDTypography>
                    <MDBox
                      sx={{
                        border: "1px solid",
                        borderColor: "grey.300",
                        borderRadius: "10px",
                        p: 1.5,
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: 120,
                        height: 80,
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      }}
                    >
                      <img
                        src={logoUrl}
                        alt="Merchant Logo"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </MDBox>
                  </>
                ) : (
                  <MDTypography variant="body2" color="text">
                    No merchant logo uploaded.
                  </MDTypography>
                );
              })()}
            </CustomTabPanel>

            {/* ── Tab 3: Users ── */}
            <CustomTabPanel value={tabValue} index={3} contentSx={{ px: 0, pt: 5 }}>
              <DataTable
                table={{ columns: merchantAllUsersTablecolumns, rows: users }}
                showTotalEntries
                isSorted
                noEndBorder
                hideExpandColumn
                entriesPerPage
                entriesPerPagePosition="bottom"
              />
            </CustomTabPanel>

            {/* ── Tab 4: QR ── */}
            <CustomTabPanel value={tabValue} index={4} contentSx={{ px: 0, pt: 5 }}>
              {accountInfo?.qr ? (
                <MDBox textAlign="center" py={2}>
                  <img
                    src={`data:image/png;base64,${accountInfo.qr}`}
                    alt="Merchant QR Code"
                    style={{ width: "100%", maxWidth: 260, height: "auto", display: "block", margin: "0 auto 16px" }}
                  />
                  <MDTypography variant="button" fontWeight="bold" display="block" sx={{ textTransform: "none" }}>
                    MID: {accountInfo.mid}
                  </MDTypography>
                  {accountInfo.paymentLink && (
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      Payment Link: {accountInfo.paymentLink}
                    </MDTypography>
                  )}
                </MDBox>
              ) : (
                <MDBox py={6} textAlign="center">
                  <MDTypography variant="body2" color="text">
                    {accountInfo ? "No QR code available." : "Loading QR info…"}
                  </MDTypography>
                </MDBox>
              )}
            </CustomTabPanel>
          </>
        ) : (
          <MDBox py={6} textAlign="center">
            <MDTypography variant="body2" color="text">Loading merchant details…</MDTypography>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MyMerchant;
